/**
 * RecordPaymentDialog — modal to record a vendor payment against the
 * current bill. Triggered from the bill detail page. Mirror of
 * `features/receipts/record-receipt-dialog.tsx` on the purchase side.
 *
 * V1 scope: single-bill allocation (the modal opens from one bill's
 * context). The domain supports multi-bill, but a multi-bill composer is
 * a different surface (lives on a future `/procurement/payments/new` page when needed).
 *
 * TDS is computed by the server — the operator enters the gross paid and
 * sees `tdsPaise`/`netPaise` in the success toast. We pre-fill the gross
 * with the bill outstanding so the common "pay the whole thing" case is
 * one click.
 *
 * Above the per-Company threshold (₹25,000 in V1.0), the server returns
 * `status='awaiting_approval'`. The UI flips its toast to "Submitted for
 * founder approval" and stays open with the approval id surfaced so the
 * operator knows what to look for in /approvals.
 *
 * Resubmit threading: on open, the dialog queries for an already-approved
 * `vendor_payment_above_threshold` approval against this bill. If one
 * exists, the form is prefilled from the stored payload, a green "approved
 * · ready to record" alert appears, and `approvalId` is passed on submit
 * to bypass the threshold gate. Without this, each resubmit silently files
 * a duplicate pending approval and the bill never flips to paid via the UI.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogTitle,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";

const MODE_OPTIONS = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
] as const;

type Mode = (typeof MODE_OPTIONS)[number]["value"];

export interface RecordPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bill: {
    id: string;
    vendorId: string;
    vendorName: string;
    vendorDocNumber: string | null;
    totalPaise: string;
    /** Total paid against this bill so far (paise). Drives outstanding. */
    paidSoFarPaise: string;
  };
  /** Called after a successful record. Parent invalidates queries. */
  onRecorded: () => void;
}

export function RecordPaymentDialog({
  open,
  onOpenChange,
  bill,
  onRecorded,
}: RecordPaymentDialogProps) {
  const outstandingPaise = BigInt(bill.totalPaise) - BigInt(bill.paidSoFarPaise);
  const [paidOn, setPaidOn] = useState(() => todayIso());
  const [mode, setMode] = useState<Mode>("neft");
  const [reference, setReference] = useState("");
  const [grossRupees, setGrossRupees] = useState("");
  // Server returns this on success — drives the awaiting-approval state.
  const [awaitingApprovalId, setAwaitingApprovalId] = useState<string | null>(null);
  // When the founder has already approved, this carries the approvalId
  // through submit so the gate doesn't fire again.
  const [approvedApprovalId, setApprovedApprovalId] = useState<string | null>(null);
  // Funding account the net debits — bank / cash / credit_card. A `card`
  // payment lands the spend on the credit-card account as liability.
  const [bankAccountId, setBankAccountId] = useState<string>("");

  // Active accounts to pick the "paid from" source. Enabled only while open.
  const accountsQ = trpc.bankAccounts.list.useQuery({}, { enabled: open });
  const accounts = accountsQ.data?.rows ?? [];

  // On open, ask the server if there's an approved-and-waiting approval
  // for this bill. The query is enabled only while open to avoid noise.
  const approvedApproval = trpc.vendorPayments.findApprovedApprovalForBill.useQuery(
    { vendorId: bill.vendorId, billId: bill.id },
    { enabled: open },
  );

  // Pre-fill gross with the outstanding balance when the dialog opens.
  // If the server reports a pre-approved approval for this bill, prefer
  // its stored payload over the default outstanding-balance prefill.
  useEffect(() => {
    if (!open) return;
    setAwaitingApprovalId(null);
    const pre = approvedApproval.data ?? null;
    if (pre) {
      setApprovedApprovalId(pre.approvalId);
      setPaidOn(pre.paidOn);
      setMode(pre.mode as Mode);
      setReference(pre.reference ?? "");
      setGrossRupees(paiseToRupeeInput(BigInt(pre.grossPaise)));
    } else {
      setApprovedApprovalId(null);
      setPaidOn(todayIso());
      setMode("neft");
      setReference("");
      setGrossRupees(paiseToRupeeInput(outstandingPaise));
    }
    // Only re-run when the dialog opens fresh or the approval result lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bill.id, approvedApproval.data?.approvalId]);

  // Default the funding account once accounts load: a `card` payment defaults
  // to the first credit card, everything else to the primary bank account.
  // Re-runs when the dialog opens, accounts arrive, or the mode flips.
  useEffect(() => {
    if (!open || accounts.length === 0) return;
    const preferred =
      mode === "card"
        ? (accounts.find((a) => a.type === "credit_card") ?? accounts.find((a) => a.type === "bank"))
        : (accounts.find((a) => a.type === "bank") ?? accounts.find((a) => a.type !== "credit_card"));
    setBankAccountId(preferred?.id ?? accounts[0]?.id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accounts.length, mode]);

  const grossPaise = useMemo(() => rupeesToPaise(grossRupees), [grossRupees]);

  // Validation
  const validationError = useMemo(() => {
    if (grossPaise <= 0n) return "Amount must be greater than zero.";
    if (grossPaise > outstandingPaise) {
      return `Amount ${formatRupees(grossPaise)} exceeds outstanding ${formatRupees(outstandingPaise)}.`;
    }
    return null;
  }, [grossPaise, outstandingPaise]);

  const record = trpc.vendorPayments.record.useMutation({
    onSuccess: (data) => {
      if (data.status === "awaiting_approval") {
        // Stay open, flip to awaiting-approval mode.
        setAwaitingApprovalId(data.id);
      } else {
        onRecorded();
        onOpenChange(false);
      }
    },
  });

  // Any edit after prefill invalidates the approval (server-side
  // `samePaymentIntent` would refuse the resubmit anyway — clearing here
  // hides the green alert and lets the threshold gate fire on submit).
  const clearApprovalOnEdit = () => {
    if (approvedApprovalId) setApprovedApprovalId(null);
  };

  const handleSubmit = () => {
    if (validationError) return;
    record.mutate({
      vendorId: bill.vendorId,
      paidOn,
      grossPaise: grossPaise.toString(),
      mode,
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(bankAccountId ? { bankAccountId } : {}),
      allocations: [
        {
          billId: bill.id,
          paise: grossPaise.toString(),
        },
      ],
      ...(approvedApprovalId ? { approvalId: approvedApprovalId } : {}),
    });
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-5 flex flex-col gap-4">
        <DialogTitle>Record payment</DialogTitle>

        <p className="text-sm text-muted -mt-2">
          {bill.vendorName} · {bill.vendorDocNumber ?? "— no doc number —"} · outstanding{" "}
          <span className="font-mono">{formatRupees(outstandingPaise)}</span>
        </p>

        {awaitingApprovalId ? (
          <Alert
            intent="warn"
            title="Submitted for founder approval"
            description={
              `This amount is above your founder threshold. ` +
              `Once the founder approves in /approvals, resubmit this payment to record it. ` +
              `(Approval id: ${awaitingApprovalId})`
            }
          />
        ) : approvedApprovalId ? (
          <Alert
            intent="ok"
            title="Founder approved — ready to record"
            description={
              `This payment was approved in /approvals. Confirm the details ` +
              `below to record it. Editing any field clears the approval and ` +
              `files a new one.`
            }
          />
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field helper="Date the money left your bank.">
            <Label htmlFor="vp-paid-on">Paid on</Label>
            <Input
              id="vp-paid-on"
              type="date"
              value={paidOn}
              onChange={(e) => {
                setPaidOn(e.target.value);
                clearApprovalOnEdit();
              }}
              className="font-mono"
              disabled={!!awaitingApprovalId}
            />
          </Field>
          <Field>
            <Label htmlFor="vp-mode-trigger">Mode</Label>
            <Select
              value={mode}
              onValueChange={(v) => {
                setMode(v as Mode);
                clearApprovalOnEdit();
              }}
              disabled={!!awaitingApprovalId}
            >
              <SelectTrigger id="vp-mode-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field helper="The account the money leaves from. A card payment lands the spend on that credit card as liability.">
          <Label htmlFor="vp-account-trigger">Paid from</Label>
          <Select
            value={bankAccountId}
            onValueChange={(v) => {
              setBankAccountId(v);
              clearApprovalOnEdit();
            }}
            disabled={!!awaitingApprovalId || accounts.length === 0}
          >
            <SelectTrigger id="vp-account-trigger" aria-label="Paid from account">
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                  {a.type === "credit_card" ? " · card" : a.type === "cash" ? " · cash" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field helper="NEFT UTR, UPI txn id, cheque number, etc.">
          <Label htmlFor="vp-reference">Reference (optional)</Label>
          <Input
            id="vp-reference"
            type="text"
            maxLength={64}
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
              clearApprovalOnEdit();
            }}
            placeholder="NEFT: N12345…  ·  Cheque: 0046123  ·  …"
            disabled={!!awaitingApprovalId}
          />
        </Field>

        <Field
          helper={
            "Gross amount paid. TDS (if any) is computed from the vendor’s " +
            "section and surfaced after submit."
          }
        >
          <Label htmlFor="vp-gross">Amount paid (₹)</Label>
          <Input
            id="vp-gross"
            type="text"
            inputMode="numeric"
            value={grossRupees}
            onChange={(e) => {
              setGrossRupees(sanitizeRupees(e.target.value));
              clearApprovalOnEdit();
            }}
            className="font-mono"
            disabled={!!awaitingApprovalId}
          />
        </Field>

        {validationError && !awaitingApprovalId ? (
          <Alert intent="warn" description={validationError} />
        ) : null}
        {record.error ? (
          <Alert intent="bad" title="Couldn't save payment" description={record.error.message} />
        ) : null}

        <div className="flex items-center gap-2 justify-end pt-1">
          <Button intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {awaitingApprovalId ? "Close" : "Cancel"}
          </Button>
          {awaitingApprovalId ? null : (
            <Button
              intent="primary"
              size="sm"
              disabled={!!validationError || record.isPending}
              onClick={handleSubmit}
            >
              {record.isPending ? "Recording…" : "Record payment"}
            </Button>
          )}
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Keep digits + a single dot with at most 2 decimals (rupees.paise). */
function sanitizeRupees(raw: string): string {
  const v = raw.replace(/[^\d.]/g, "");
  const [whole = "", ...rest] = v.split(".");
  return rest.length > 0 ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
}

/**
 * Rupee text → bigint paise. Accepts paise (e.g. "92566.99" → 9256699n) so a
 * GST bill with a non-whole-rupee total can be settled exactly. Returns 0n on
 * empty / invalid.
 */
function rupeesToPaise(text: string): bigint {
  const cleaned = text.replace(/[,\s]/g, "");
  if (cleaned === "" || cleaned === "." || !/^\d*(\.\d{0,2})?$/.test(cleaned)) return 0n;
  const [whole = "", frac = ""] = cleaned.split(".");
  const rupees = whole === "" ? 0n : BigInt(whole);
  const paiseFrac = BigInt((frac + "00").slice(0, 2));
  return rupees * 100n + paiseFrac;
}

function formatRupees(paise: bigint): string {
  const neg = paise < 0n;
  const abs = neg ? -paise : paise;
  const rupees = (abs / 100n).toLocaleString("en-IN");
  const p = (abs % 100n).toString().padStart(2, "0");
  return `${neg ? "-" : ""}₹${rupees}.${p}`;
}

/** Paise → editable rupee string for the input default (keeps paise). */
function paiseToRupeeInput(paise: bigint): string {
  const rupees = paise / 100n;
  const p = paise % 100n;
  return p === 0n ? rupees.toString() : `${rupees}.${p.toString().padStart(2, "0")}`;
}
